import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import PageLayout from '../components/PageLayout';
import PageBanner from '../components/PageBanner';
import SEO from '../components/SEO';
import API, { trackView } from '../api';
import { useLanguage } from '../context/LanguageContext';

const BASE = process.env.REACT_APP_API_URL?.replace('/api', '') ?? 'http://localhost:5000';

export default function ProductCategory() {
  const { t } = useLanguage();
  const { categorySlug } = useParams();
  const [brands, setBrands] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const trackedId = useRef(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      API.get(`/categories`),
      API.get(`/brands?category=${categorySlug}`),
    ]).then(([cats, brs]) => {
      const cat = cats.data.find(c => c.slug === categorySlug);
      setCategory(cat);
      setBrands(brs.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [categorySlug]);

  // Fires once the real category id is known, and only once per id — guards
  // against both StrictMode's dev double-invoke and this effect re-running
  // for unrelated reasons.
  useEffect(() => {
    if (category?.id && trackedId.current !== category.id) {
      trackedId.current = category.id;
      trackView('category', category.id);
    }
  }, [category?.id]);

  const imgSrc = (url) => url?.startsWith('/') ? `${BASE}${url}` : url;

  return (
    <PageLayout>
      <SEO title={category?.name} description={category?.description} />
      <PageBanner en="OUR PRODUCTS"
        title={category?.name || t('common.products')}
        breadcrumbs={[{ label: t('common.products'), to: '/products' }, { label: category?.name || categorySlug }]}
      />

      <section className="pt-6 pb-20 lg:pb-28 bg-white">
        <div className="w-[90%] max-w-7xl mx-auto">
          {category?.description && (
            <div className="mb-10 max-w-2xl">
              <p className="text-gray-600">{category.description}</p>
            </div>
          )}

          {!loading && brands.length > 0 && (
            <h2 className="section-title mb-10">{t('common.brand')}</h2>
          )}

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-48 bg-gray-100 animate-pulse" />)}
            </div>
          ) : brands.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400 text-lg mb-2">{t('products.noBrands')}</p>
              <p className="text-gray-400 text-sm mb-6">{t('common.pleaseContact')}</p>
              <Link to="/contact" className="btn-primary text-sm">{t('common.contactUs')}</Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
              {brands.map((b, i) => (
                <Link key={b.id} to={`/products/${categorySlug}/${b.slug}`}
                  className="card fade-up group p-6 flex flex-col items-center gap-4 text-center"
                  style={{ animationDelay: `${i * 0.08}s` }}>
                  <div className="h-16 w-full flex items-center justify-center">
                    {b.logo_url ? (
                      <img src={imgSrc(b.logo_url)} alt={b.name}
                        className="max-h-16 max-w-full object-contain grayscale group-hover:grayscale-0 transition-all duration-300" />
                    ) : (
                      <span className="font-bold text-ink text-lg">{b.name}</span>
                    )}
                  </div>
                  <div>
                    <h3 className="font-bold text-ink text-sm mb-1">{b.name}</h3>
                    {b.description && <p className="text-xs text-gray-500 line-clamp-2">{b.description}</p>}
                  </div>
                  <span className="flex items-center gap-1.5 text-xs text-primary group-hover:text-primary transition-colors font-medium mt-auto pt-2 border-t border-gray-100 w-full justify-center">
                    {t('common.viewProducts')} <ArrowRightIcon className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>
          )}

          {/* Enquiry CTA */}
          <div className="mt-20 bg-[#f5f5f5] p-10 text-center">
            <h3 className="text-xl font-bold text-ink mb-2">{t('common.cantFindTitle')}</h3>
            <p className="text-gray-600 text-sm mb-5">{t('common.cantFindSubtitle')}</p>
            <Link to="/contact" className="btn-primary btn-arrow text-sm">{t('common.contactUs')}</Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
